import React, { useCallback, useEffect, useRef, useState } from "react";
import { colors } from "../utils/theme.js";
import { api } from "../api/client.js";
import { ghostBtn } from "../clients/ui.js";
import ConfirmDialog from "../clients/ConfirmDialog.jsx";
import SectionBlock from "./SectionBlock.jsx";
import { paper, sans } from "./docTheme.js";

const btn = { padding: "6px 12px", borderRadius: 6, border: `1px solid ${paper.line}`, background: paper.card, color: paper.inkSoft, fontFamily: sans, fontSize: 13, cursor: "pointer" };
const noop = async () => ({ ok: true });
const STILL_SAVING = "Still saving — try again in a moment.";
const CONFLICT = "Someone else saved this template — their changes are loaded; save again to apply yours.";

// The newest saved Kickoff template, or the built-in default the API returns.
async function fetchKickoff() {
  const r = await api.meetingTemplates.list();
  const fallback = r?.defaults?.kickoff || null;
  const saved = (r?.items || []).filter((t) => t.type === "kickoff")
    .sort((a, b) => (String(a.updatedAt || "") < String(b.updatedAt || "") ? 1 : -1))[0];
  return { tmpl: saved || fallback, fallback };
}

// Admin page (spec §3.6): the Kickoff template that new Kickoff meetings copy. Until an admin
// saves it, meetings use the built-in default the API returns as `defaults.kickoff`; the first
// save stores it. Edits never change meetings already created — they hold copies.
export default function MeetingTemplatesPage({ users }) {
  const [tmpl, setTmpl] = useState(null); // saved template ({ id, _etag, … }) or the default
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  // Bumped on every successful save and passed to the sections as their rev, so a section's
  // stale view-mode error clears once the template has moved on.
  const [saveCount, setSaveCount] = useState(0);
  const defaultRef = useRef(null);
  const busyRef = useRef(false); // one save at a time: every save sends the same _etag

  const load = useCallback(async () => {
    try {
      const r = await fetchKickoff();
      defaultRef.current = r.fallback;
      setTmpl(r.tmpl);
      setLoadError(null);
    } catch {
      setLoadError("Couldn’t load the meeting templates.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // The template is small and only admins edit it, so every change saves the whole thing.
  // Results go back to the caller: section actions show them in the section, page actions
  // (add section, restore default) on the page — never both.
  const persist = async (sections) => {
    if (busyRef.current) return { ok: false, error: STILL_SAVING };
    busyRef.current = true; setBusy(true);
    try {
      const body = { name: tmpl.name || "Kickoff", type: "kickoff", sections };
      const res = tmpl.id ? await api.meetingTemplates.update(tmpl.id, body, tmpl._etag) : await api.meetingTemplates.create(body);
      if (res.ok) { setTmpl(res.data); setSaveCount((n) => n + 1); setError(null); return { ok: true }; }
      if (res.status === 409) {
        const fromDefault = !tmpl.id; // theirs has different section ids: the calling section will unmount
        // Load theirs (and its etag). Sections are keyed by id, so open section drafts survive.
        try {
          const r = await fetchKickoff();
          defaultRef.current = r.fallback;
          setTmpl(r.tmpl);
          if (fromDefault) setError(CONFLICT);
          return { ok: false, error: CONFLICT };
        } catch {
          return { ok: false, error: res.data?.error || "Someone else saved this template — reload to see their changes." };
        }
      }
      return { ok: false, error: res.data?.error || "Couldn’t save the template." };
    } finally { busyRef.current = false; setBusy(false); }
  };

  if (loadError) {
    return (
      <div style={{ padding: 40, color: colors.danger, display: "flex", alignItems: "center", gap: 12 }}>
        <span>{loadError}</span>
        <button type="button" style={ghostBtn} onClick={load}>Retry</button>
      </div>
    );
  }
  if (!tmpl) return <div style={{ padding: 40, color: colors.textMuted }}>Loading templates…</div>;

  const sections = tmpl.sections || [];
  const isDefault = !tmpl.id;
  const at = (i, next) => sections.map((s, j) => (j === i ? next : s));
  const swap = (i, d) => { const n = [...sections]; [n[i], n[i + d]] = [n[i + d], n[i]]; return persist(n); };
  const addSection = async () => {
    const r = await persist([...sections, { title: newTitle.trim(), kind: "standard", items: [] }]);
    if (r.ok) setNewTitle(""); else setError(r.error);
  };
  const restoreDefault = async () => {
    setConfirmReset(false);
    const r = await persist(defaultRef.current.sections);
    if (!r.ok) setError(r.error);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>Kickoff meeting template</div>
          <div style={{ fontSize: 13, color: colors.textMuted }}>
            {isDefault
              ? "Not saved yet — new Kickoff meetings use this default. Saving any change stores it."
              : "New Kickoff meetings copy this template. Changing it never changes meetings already created."}
          </div>
        </div>
        {!isDefault && <button type="button" style={ghostBtn} onClick={() => setConfirmReset(true)} disabled={busy}>Restore default</button>}
      </div>
      {error && <div role="alert" style={{ color: colors.danger, fontSize: 13 }}>{error}</div>}
      <div style={{ background: paper.bg, color: paper.ink, fontFamily: sans, borderRadius: 12, border: `1px solid ${paper.line}`, padding: "32px 48px" }}>
        {sections.map((s, i) => (
          <SectionBlock key={s.id} section={{ ...s, rev: saveCount }} index={i + 1} editable users={users} agendaInteractive={false} actionsDisabled={busy}
            onSave={(fields) => persist(at(i, { ...s, ...fields }))}
            onRemove={() => persist(sections.filter((_, j) => j !== i))}
            onMoveUp={i > 0 ? () => swap(i, -1) : undefined}
            onMoveDown={i < sections.length - 1 ? () => swap(i, 1) : undefined}
            onToggleAgenda={noop} onClearReview={noop} />
        ))}
        <div style={{ display: "flex", gap: 8, paddingTop: 24 }}>
          <input aria-label="New section title" maxLength={120} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New section title"
            style={{ flex: 1, padding: "8px 10px", border: `1px solid ${paper.line}`, borderRadius: 6, fontFamily: sans, fontSize: 14 }} />
          <button type="button" style={btn} disabled={!newTitle.trim() || busy} onClick={addSection}>+ Add section</button>
        </div>
      </div>
      {confirmReset && (
        <ConfirmDialog title="Restore the default template?" confirmLabel="Restore default" onCancel={() => setConfirmReset(false)} onConfirm={restoreDefault}>
          The Kickoff template goes back to the built-in Summary, Tips for Success! and Agenda. Meetings already created are not changed.
        </ConfirmDialog>
      )}
    </div>
  );
}
